// src/shared/streaming/TelemetryCache.ts
// ────────────────────────────────────────────────────────────────────────────
// In-memory time-series cache with range tracking and delta computation.
//
// Stores decoded telemetry points indexed by composite key (device + metric).
// When the visible time window shifts, the cache computes what ranges are
// already available and returns only the *delta* spans that need fetching,
// enabling instant render of overlap data while missing ranges stream in.
//
// DRY — works for any numeric time-series, not IoT-specific.
// ────────────────────────────────────────────────────────────────────────────

import type { TimeSeriesPoint } from './TelemetryCodec';
import type { MetricKey } from './TelemetryCodec';

/** Range of epoch-ms. */
export interface TimeRange {
  from: number; // inclusive epoch-ms
  to:   number; // inclusive epoch-ms
}

/** A contiguous cached span for one key. */
interface CacheEntry {
  points:    TimeSeriesPoint[];    // sorted oldest → newest
  rangeFrom: number;               // earliest ts in points
  rangeTo:   number;               // latest ts in points
  fetchedAt: number;               // Date.now() when last updated
}

/** Composite cache key. */
function cacheKey(deviceId: string, metric: MetricKey): string {
  return `${deviceId}:${metric}`;
}

/**
 * TelemetryCache — singleton per logical view component.
 *
 * Usage:
 *   const cache = new TelemetryCache();
 *   // Check what we already have
 *   const cached = cache.query('dev-1', 'temp', { from, to });
 *   const deltas = cache.missingRanges('dev-1', 'temp', { from, to });
 *   // Merge streamed data
 *   cache.merge('dev-1', 'temp', newPoints);
 */
export class TelemetryCache {
  private store = new Map<string, CacheEntry>();

  /** Max age in ms — entries older than this are evicted on access. */
  private maxAgeMs: number;

  /** Max total points per entry (ring buffer). Oldest trimmed on merge. */
  private maxPoints: number;

  constructor(opts?: { maxAgeMs?: number; maxPoints?: number }) {
    this.maxAgeMs  = opts?.maxAgeMs  ?? 10 * 60 * 1000; // 10 min default
    this.maxPoints = opts?.maxPoints ?? 100_000;
  }

  // ── Query ────────────────────────────────────────────────────────────

  /**
   * Return all cached points for a device + metric within a time range.
   * Returns empty array if nothing cached.
   */
  query(deviceId: string, metric: MetricKey, range: TimeRange): TimeSeriesPoint[] {
    const entry = this.getEntry(deviceId, metric);
    if (!entry) return [];
    return this.sliceRange(entry.points, range);
  }

  /**
   * Compute the time ranges NOT covered by the cache for a given window.
   * Returns 0, 1, or 2 ranges (left gap, right gap, or both).
   */
  missingRanges(deviceId: string, metric: MetricKey, range: TimeRange): TimeRange[] {
    const entry = this.getEntry(deviceId, metric);
    if (!entry) return [range]; // nothing cached

    const gaps: TimeRange[] = [];

    // Left gap: requested from < cached from
    if (range.from < entry.rangeFrom) {
      gaps.push({ from: range.from, to: Math.min(entry.rangeFrom, range.to) });
    }

    // Right gap: requested to > cached to
    if (range.to > entry.rangeTo) {
      gaps.push({ from: Math.max(entry.rangeTo, range.from), to: range.to });
    }

    return gaps;
  }

  /**
   * Mark a time range as queried for a device + metric, even when no data
   * was returned.  Creates or extends a cache entry so that missingRanges()
   * no longer reports the range as missing — prevents perpetual re-queries
   * for devices / metrics / time-ranges that genuinely have no data.
   */
  markCovered(deviceId: string, metric: MetricKey, range: TimeRange): void {
    const key = cacheKey(deviceId, metric);
    const entry = this.store.get(key);
    if (!entry) {
      this.store.set(key, {
        points:    [],
        rangeFrom: range.from,
        rangeTo:   range.to,
        fetchedAt: Date.now(),
      });
    } else {
      entry.rangeFrom = Math.min(entry.rangeFrom, range.from);
      entry.rangeTo   = Math.max(entry.rangeTo, range.to);
      entry.fetchedAt  = Date.now();
    }
  }

  /**
   * Reset coverage marks for a device + metric back to the actual data extent.
   * Called before periodic full-window re-checks so that computeDeltas
   * reports the entire window as missing — forcing a fresh exhaustive fetch
   * that catches late-arriving data (write-buffer lag, archive recovery).
   */
  clearCoverage(deviceId: string, metric: MetricKey): void {
    const key = cacheKey(deviceId, metric);
    const entry = this.store.get(key);
    if (!entry) return;
    if (entry.points.length === 0) {
      // No data — remove the entry entirely so missingRanges reports the full range
      this.store.delete(key);
      return;
    }
    // Shrink rangeFrom/rangeTo back to actual data boundaries
    entry.rangeFrom = entry.points[0].ts;
    entry.rangeTo   = entry.points[entry.points.length - 1].ts;
  }

  /**
   * Count cached points for a device + metric within a time range.
   * Used to decide whether a fetch returned meaningful data before marking
   * coverage — prevents starvation when some devices return very few rows.
   */
  countInRange(deviceId: string, metric: MetricKey, range: TimeRange): number {
    const entry = this.getEntry(deviceId, metric);
    if (!entry) return 0;
    return this.sliceRange(entry.points, range).length;
  }

  /**
   * Check if the cache fully covers a time range for ALL metrics of a device.
   */
  isFullyCovered(deviceId: string, metrics: MetricKey[], range: TimeRange): boolean {
    for (const m of metrics) {
      if (this.missingRanges(deviceId, m, range).length > 0) return false;
    }
    return true;
  }

  // ── Merge ────────────────────────────────────────────────────────────

  /**
   * Merge new points into the cache (deduplicated, sorted).
   * Trims to maxPoints ring buffer limit if needed.
   *
   * Fast-path for the two common streaming patterns:
   *   1. Tail append — new points are all newer than existing (live tail)
   *   2. Head prepend — new points are all older (history fill)
   * Falls back to Map-based merge only for interleaved / overlapping data.
   */
  merge(deviceId: string, metric: MetricKey, newPoints: TimeSeriesPoint[]): void {
    if (!newPoints.length) return;

    // Defensive: strip undefined / malformed entries from incoming data.
    // Protects against corrupt binary decode or malformed upstream JSON.
    const valid = newPoints.filter(
      (p): p is TimeSeriesPoint => !!p && typeof p.ts === 'number' && typeof p.value === 'number' && !Number.isNaN(p.ts),
    );
    if (!valid.length) return;
    newPoints = valid;

    const key = cacheKey(deviceId, metric);
    let entry = this.store.get(key);

    // Sort incoming batch (typically small relative to cache)
    const sorted = newPoints.length === 1
      ? newPoints
      : [...newPoints].sort((a, b) => a.ts - b.ts);

    if (!entry) {
      const pts = newPoints.length === 1 ? [...sorted] : sorted;
      this.store.set(key, {
        points:    pts,
        rangeFrom: pts[0].ts,
        rangeTo:   pts[pts.length - 1].ts,
        fetchedAt: Date.now(),
      });
      return;
    }

    const existing = entry.points;

    // Guard: entry exists but has 0 points (created by markCovered for a
    // device/metric that had no data). Treat as fresh insertion — avoids
    // crash on existing[-1].ts when existing.length === 0.
    if (existing.length === 0) {
      entry.points    = sorted.length === 1 ? [...sorted] : sorted;
      entry.rangeFrom = Math.min(entry.rangeFrom, sorted[0].ts);
      entry.rangeTo   = Math.max(entry.rangeTo, sorted[sorted.length - 1].ts);
      entry.fetchedAt  = Date.now();
      return;
    }

    let merged: TimeSeriesPoint[];

    // Fast-path 1: tail append — all new points are after existing tail
    if (sorted[0].ts > existing[existing.length - 1].ts) {
      merged = existing.concat(sorted);
    }
    // Fast-path 2: head prepend — all new points are before existing head
    else if (sorted[sorted.length - 1].ts < existing[0].ts) {
      merged = sorted.concat(existing);
    }
    // Slow-path: overlapping / interleaved — dedup via Map
    else {
      const tsMap = new Map<number, TimeSeriesPoint>();
      for (const p of existing) tsMap.set(p.ts, p);
      for (const p of sorted)   tsMap.set(p.ts, p);
      merged = Array.from(tsMap.values()).sort((a, b) => a.ts - b.ts);
    }

    // Ring buffer trim — keep newest
    const trimmed = merged.length > this.maxPoints
      ? merged.slice(merged.length - this.maxPoints)
      : merged;

    entry.points    = trimmed;
    entry.rangeFrom = trimmed[0].ts;
    entry.rangeTo   = trimmed[trimmed.length - 1].ts;
    entry.fetchedAt = Date.now();
  }

  // ── Eviction ─────────────────────────────────────────────────────────

  /** Clear all cached data. */
  clear(): void {
    this.store.clear();
  }

  /** Evict a specific device's data. */
  evict(deviceId: string): void {
    const prefix = `${deviceId}:`;
    const keysToDelete: string[] = [];
    this.store.forEach((_, key) => {
      if (key.startsWith(prefix)) keysToDelete.push(key);
    });
    for (const k of keysToDelete) this.store.delete(k);
  }

  /** Evict entries older than maxAgeMs. */
  gc(): void {
    const cutoff = Date.now() - this.maxAgeMs;
    const keysToDelete: string[] = [];
    this.store.forEach((entry, key) => {
      if (entry.fetchedAt < cutoff) keysToDelete.push(key);
    });
    for (const k of keysToDelete) this.store.delete(k);
  }

  // ── Internals ────────────────────────────────────────────────────────

  private getEntry(deviceId: string, metric: MetricKey): CacheEntry | null {
    const key = cacheKey(deviceId, metric);
    const entry = this.store.get(key);
    if (!entry) return null;
    // Lazy eviction on access
    if (Date.now() - entry.fetchedAt > this.maxAgeMs) {
      this.store.delete(key);
      return null;
    }
    return entry;
  }

  private sliceRange(points: TimeSeriesPoint[], range: TimeRange): TimeSeriesPoint[] {
    if (!points.length) return [];

    // Binary search for start
    let lo = 0, hi = points.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      const p = points[mid];
      if (!p || typeof p.ts !== 'number') { lo = mid + 1; continue; }
      if (p.ts < range.from) lo = mid + 1;
      else hi = mid;
    }
    const start = lo;

    // Binary search for end
    lo = start; hi = points.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      const p = points[mid];
      if (!p || typeof p.ts !== 'number') { lo = mid + 1; continue; }
      if (p.ts <= range.to) lo = mid + 1;
      else hi = mid;
    }

    return points.slice(start, lo);
  }
}
